import {
  HoleDTO,
  HoleFeatureLegendProperties,
  HoleLegendProperties,
  HoleStatus,
  HoleStatusHistoryUtil,
} from 'src/app/services/hole.service';
import { HoleFeatureFilter, HoleLegends, HoleStatusFilter } from '../site-map.types';

export type HoleFeature = Exclude<HoleFeatureFilter, 'all'>;

const GRAY_HOLE_ICON = 'assets/icon/hole-gray.svg';
const OTHER_FEATURE_ICON = 'assets/icon/hole-others.svg';

const HOLE_ICON_BY_STATUS: Record<string, string> = {
  [HoleStatus.PLANNED]: 'assets/icon/hole-planned.svg',
  [HoleStatus.DRILLED]: 'assets/icon/hole-drilled.svg',
  [HoleStatus.LOADING]: 'assets/icon/hole-loading.svg',
  [HoleStatus.LOADED]: 'assets/icon/hole-loaded.svg',
  [HoleStatus.MISSING]: 'assets/icon/hole-missing.svg',
  [HoleStatus.TO_REDRILL]: 'assets/icon/hole-to-redrill.svg',
  [HoleStatus.BLASTED]: 'assets/icon/hole-blasted.svg',
  [HoleStatus.COLLAPSED]: 'assets/icon/hole-collapsed.svg',
  [HoleStatus.TOADJUST]: 'assets/icon/hole-to-adjust.svg',
  [HoleStatus.REDRILLED]: 'assets/icon/hole-redrilled.svg',
};

const HOLE_FEATURE_ICONS: Record<HoleFeature, string> = {
  water: 'assets/icon/hole-water.svg',
  tube: 'assets/icon/hole-tube.svg',
  deviation: 'assets/icon/hole-deviation.svg',
};

export const HOLE_STATUSES: HoleStatus[] = Object.values(HoleStatus);
export const HOLE_FEATURES: HoleFeature[] = ['water', 'tube', 'deviation'];

export const EMPTY_HOLE_LEGENDS: HoleLegends = { status: [], feature: [] };

export function holeStatusIcon(status: string): string {
  return HOLE_ICON_BY_STATUS[status] ?? GRAY_HOLE_ICON;
}

export function holeFeatureIcon(feature: string): string {
  return HOLE_FEATURE_ICONS[feature as HoleFeature] ?? OTHER_FEATURE_ICON;
}

/** Un filtre de statut porte sur l'historique du trou quand il est renseigné, sinon sur son statut courant. */
export function holeMatchesStatus(hole: HoleDTO, status: HoleStatus): boolean {
  return hole.statusHistory
    ? HoleStatusHistoryUtil.hasHadStatus(hole.statusHistory, status)
    : hole.status === status;
}

export function holeHasFeature(hole: HoleDTO, feature: HoleFeature): boolean {
  switch (feature) {
    case 'water': return !!hole.hasWater;
    case 'tube': return !!hole.hasTube;
    case 'deviation': return !!hole.hasDeviation;
  }
}

/** Icône d'un trou : celle de la caractéristique ou du statut filtré quand il y correspond, grise sinon, celle de son statut sans filtre. */
export function holeIcon(hole: HoleDTO, statusFilter: HoleStatusFilter, featureFilter: HoleFeatureFilter): string {
  if (featureFilter !== 'all') {
    return holeHasFeature(hole, featureFilter) ? HOLE_FEATURE_ICONS[featureFilter] : GRAY_HOLE_ICON;
  }
  if (statusFilter !== 'actual') {
    return holeMatchesStatus(hole, statusFilter) ? holeStatusIcon(statusFilter) : GRAY_HOLE_ICON;
  }
  return holeStatusIcon(hole.status);
}

/** Légendes du bas de carte pour les trous affichés : répartition par statut, ratio du statut filtré, ou ratio par caractéristique. */
export function computeHoleLegends(
  holes: HoleDTO[],
  statusFilter: HoleStatusFilter,
  featureFilter: HoleFeatureFilter,
): HoleLegends {
  const total = holes.length;
  if (total === 0) return EMPTY_HOLE_LEGENDS;

  if (featureFilter !== 'all') {
    const feature: HoleFeatureLegendProperties[] = HOLE_FEATURES.map(featureType => {
      const count = holes.filter(hole => holeHasFeature(hole, featureType)).length;
      return { featureType, numberOfHoles: count, percentage: Math.round((count / total) * 100), totalHoles: total };
    });
    return { status: [], feature };
  }

  if (statusFilter !== 'actual') {
    const matching = holes.filter(hole => holeMatchesStatus(hole, statusFilter)).length;
    const status: HoleLegendProperties[] = [];
    if (matching > 0) {
      status.push({ holeStatus: statusFilter, numberOfHoles: matching, percentage: Math.round((matching / total) * 100), totalHoles: total });
    }
    if (total - matching > 0) {
      const others = total - matching;
      status.push({ holeStatus: 'Other' as HoleStatus, numberOfHoles: others, percentage: Math.round((others / total) * 100), totalHoles: total });
    }
    return { status, feature: [] };
  }

  const counts = new Map<string, number>();
  for (const hole of holes) {
    counts.set(hole.status, (counts.get(hole.status) ?? 0) + 1);
  }
  return {
    status: [...counts].map(([holeStatus, numberOfHoles]) => ({ holeStatus: holeStatus as HoleStatus, numberOfHoles })),
    feature: [],
  };
}
